import React, { useEffect, useState } from 'react';
import { Modal, Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { format as formatDate } from 'date-fns';
import { handleCustomerProjectClick } from 'pxc-editor-web/environment_modules/web_component/actions/project';
import { colors } from '@/globalStyles';
import { LOADING, SUCCESS } from '@/constants';
import { getConfig } from '@/config/runtimeConfig';
import { pxcPortalApi } from '@/endpoints/apiUrls';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import { toQueryParams, openLink } from '@/helpers';
import { setSaveProjectModalOpen } from '@/store/actions/cmme';
import { Button, DownloadIcon, CircleCheckIcon, LoadingIcon, Input, SupportIcon, Label } from '@/components';
import WebComponent from '@/endpoints/webComponent';
import Analytics from '@/analytics';
import { ModalWrapperStyled, ModalBodyStyled, ModalFooterStyled, LoaderSpiner, IconWrapperStyled, TitleStyled, DescriptionStyled } from './styles';

const getFileName = value => {
  const matfileTemplate = getBuildedMatfileTemplate();

  return value || `${matfileTemplate?.name} ${formatDate(new Date(), 'yyyy-MM-dd')}`;
};

const WebSaveProjectModal = () => {
  const addToCartStatus = useSelector((state) => state.cmmeReducer.addToCartStatus);
  const webComponentConfing = useSelector((state) => state.cmmeReducer.config);
  const colorSchema = useSelector((state) => state.core.colorSchema);
  const dispatch = useDispatch();
  const [projectNameToSave, setProjectNameToSave] = useState(getFileName());
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    handleCustomerAccountsFetch();
  }, []);

  const handleCustomerAccountsFetch = async () => {
    try {
      const response = await WebComponent.getCustomerAccounts(webComponentConfing);
      if (response?.allAccounts?.length) {
        setAccounts(response.allAccounts);
      }
    } catch (error) {
      console.error('[handleCustomerAccountsFetch]:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleClose = () => {
    dispatch(setSaveProjectModalOpen(false));
  };

  const handleAddToCartClick = () => {
    const projectName = getFileName(projectNameToSave);

    dispatch(handleCustomerProjectClick({ filename: projectName, accounts }));

    Analytics.configureMarkingEditorSave({ articleNumber: colorSchema?.article_number || '-', projectName });
  };

  const handleSuccessClick = () => {
    const _realm = webComponentConfing.realm;
    const _locale = webComponentConfing.locale;
    const params = toQueryParams({ _realm, _locale });
    const link = pxcPortalApi.mySolutionsPage(params);

    window.location.assign(link);

    Analytics.linkInbound({ linkType: 'Ok', linkUrl: link });

    handleClose();
  };

  const handleProjectNameChange = e => {
    setProjectNameToSave(e.target.value);
  };

  const handleContinueEditingClick = () => {
    Analytics.linkInbound({ linkType: 'Continue editing' });

    handleClose();
  };

  const handleCustomerSupportClick = () => {
    const { customerSupportUrl } = getConfig();

    openLink(customerSupportUrl);

    Analytics.linkInbound({ linkType: 'Contact support', linkUrl: customerSupportUrl });
  };

  if (isLoading) {
    return null;
  } else {
    switch (addToCartStatus) {
      case LOADING: {
        return (
          <Modal open>
            <ModalWrapperStyled>
              <ModalBodyStyled>
                <Box>
                  <Box sx={{ marginTop: '20px', textAlign: 'center' }}>
                    <LoaderSpiner>
                      <LoadingIcon />
                    </LoaderSpiner>
                  </Box>
                  <Box sx={{ marginTop: '40px', marginBottom: '50px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <Label variant="bold" fontSize={24}>
                        <FormattedMessage id="cmme.saveYourProject" defaultMessage="Saving your project..." />
                      </Label>
                    </div>
                    <div>
                      <Label fontSize={18}>
                        <FormattedMessage id="cmme.pleaseWait" defaultMessage="Please wait" />
                      </Label>
                    </div>
                  </Box>
                </Box>
              </ModalBodyStyled>
  
              <ModalFooterStyled></ModalFooterStyled>
            </ModalWrapperStyled>
          </Modal>
        );
      }
      case SUCCESS: {
        return (
          <Modal open>
            <ModalWrapperStyled>
              <ModalBodyStyled>
                <Box>
                  <Box sx={{ marginTop: '20px', textAlign: 'center' }}>
                    <CircleCheckIcon />
                  </Box>
                  <Box sx={{ marginTop: '40px', marginBottom: '50px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <Label variant="bold" fontSize={24}>
                        <FormattedMessage id="cmme.projectSavedSuccessfully" defaultMessage="Project was saved successfully" />
                      </Label>
                    </div>
                    <div>
                      <Label fontSize={18}>
                        <FormattedMessage id="cmme.redirectToMySolution" defaultMessage="You will be redirected to My solution page" />
                      </Label>
                    </div>
                  </Box>
                </Box>
              </ModalBodyStyled>
  
              <ModalFooterStyled status={SUCCESS}>
                <Button variant="secondary" onClick={handleSuccessClick}>
                  <FormattedMessage id="core.ok" defaultMessage="Ok" />
                </Button>
              </ModalFooterStyled>
            </ModalWrapperStyled>
          </Modal>
        );
      }
      default: {
        if (!accounts.length) {
          const part1 = intl.formatMessage({ id: 'cmme.contactSupport.noCustomerFound', defaultMessage: 'No customer number found' });
          const part2 = intl.formatMessage({ id: 'cmme.contactSupport.pleaseContactSupport', defaultMessage: 'please contact the support' });
  
          return (
            <Modal open onClose={handleClose}>
              <ModalWrapperStyled>
                <ModalBodyStyled>
                  <Box>
                    <IconWrapperStyled>
                      <SupportIcon width={100} height={100} />
                    </IconWrapperStyled>
                    <Box sx={{ textAlign: 'center', margin: '0px 50px' }}>
                      <TitleStyled>
                        <FormattedMessage id="cmme.contactSupport.title" defaultMessage="Contact support" />
                      </TitleStyled>
                      <DescriptionStyled>
                        <span>{`${part1}, `}<a href="#" onClick={handleCustomerSupportClick}>{part2}</a>.</span>
                      </DescriptionStyled>
                    </Box>
                  </Box>
                  <Box sx={{ width: '100%', marginTop: '20px' }}>
                    <Input
                      value={projectNameToSave}
                      placeholder={intl.formatMessage({ id: 'cmme.saveProjectPlaceholder', defaultMessage: 'Material Description/current date' })}
                      onChange={handleProjectNameChange}
                    />
                  </Box>
                </ModalBodyStyled>
                <ModalFooterStyled>
                  <Button variant="primary" onClick={handleContinueEditingClick}>
                    <FormattedMessage id="cmme.continueEditing" defaultMessage="Continue editing" />
                  </Button>
                  <Button variant="secondary" disabled>
                    <FormattedMessage id="app.footer.button.save" defaultMessage="Save" />
                  </Button>
                </ModalFooterStyled>
              </ModalWrapperStyled>
            </Modal>
          );
        }
        return (
          <Modal open onClose={handleClose}>
            <ModalWrapperStyled>
              <ModalBodyStyled>
                <Box>
                  <IconWrapperStyled>
                    <DownloadIcon width={100} height={100} color={colors.quickSilver} />
                  </IconWrapperStyled>
                  <Box sx={{ textAlign: 'center' }}>
                    <TitleStyled>
                      <FormattedMessage id="app.header.menu.saveProject" defaultMessage="Saving project" />
                    </TitleStyled>
                    <DescriptionStyled>
                      <FormattedMessage id="cmme.afterSavingMessage" defaultMessage="After saving, the project can no longer be edited. You will be redirected to “My Solutions”." />
                    </DescriptionStyled>
                    <div style={{ lineHeight: 1.3, marginBottom: '40px', color: colors.dimGray }}>
                      <Label fontSize={16}>
                        <FormattedMessage
                          id="cmme.bySavingProject"
                          defaultMessage="Note: By saving the project, you confirm that the selection of the marking system is reserved for Phoenix Contact."
                        />
                      </Label>
                    </div>
                    <Input
                      autoFocus={true}
                      value={projectNameToSave}
                      placeholder={intl.formatMessage({ id: 'cmme.saveProjectPlaceholder', defaultMessage: 'Material Description/current date' })}
                      onChange={handleProjectNameChange}
                    />
                  </Box>
                </Box>
              </ModalBodyStyled>
              <ModalFooterStyled>
                <Button variant="primary" onClick={handleContinueEditingClick}>
                  <FormattedMessage id="cmme.continueEditing" defaultMessage="Continue editing" />
                </Button>
                <Button variant="secondary" onClick={handleAddToCartClick}>
                  <FormattedMessage id="app.footer.button.save" defaultMessage="Save" />
                </Button>
              </ModalFooterStyled>
            </ModalWrapperStyled>
          </Modal>
        );
      }
    }
  }
};

export default WebSaveProjectModal;
